import { useState, useCallback } from 'react';
import { Check, Flame, FlaskConical, AlertTriangle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Beaker } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Safety with Strong Bases',
    content: 'Potassium hydroxide (KOH) is a strong base that can cause severe chemical burns. Always wear appropriate PPE including safety goggles and gloves. The reaction produces hydrogen gas (H2), which is highly flammable - keep away from open flames and ignition sources.',
    formulas: ['KOH -> K+ + OH-', '2Al + 2K+ + 2OH- + 6H₂O -> 2K+ + 2Al(OH)4- + 3H2(g)']
  },
  2: {
    title: 'Aluminum as an Amphoteric Metal',
    content: 'Aluminum is an amphoteric metal, meaning it can react with both acids and bases. When added to KOH solution, the protective aluminum oxide layer first dissolves, then the aluminum metal reacts. The reaction is exothermic and produces hydrogen gas bubbles.',
    formulas: ['Al₂O₃ + 2OH- + 3H₂O -> 2[Al(OH)4]-']
  },
  3: {
    title: 'Potassium Hydroxide Solution',
    content: 'KOH solution provides the hydroxide ions needed to dissolve aluminum. Always add reagents slowly and in a well-ventilated area.',
    formulas: ['KOH(s) -> K+(aq) + OH-(aq)']
  },
  4: {
    title: 'Hydrogen Gas Evolution',
    content: 'The bubbles you observe are hydrogen gas (H2) being released. This gas is colorless, odorless, and highly flammable. The reaction rate decreases as aluminum is consumed. The solution turns cloudy initially but clears as potassium aluminate forms.',
    formulas: ['2Al + 2K+ + 2OH- + 6H₂O -> 2K+ + 2Al(OH)4- + 3H2', 'H2 + 1/2O2 -> H₂O (combustion)']
  }
};

export default function Part1Dissolution() {
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [aluminumAdded, setAluminumAdded] = useState(false);
  const [kohAdded, setKohAdded] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [reactionComplete, setReactionComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [safetyAcknowledged, aluminumAdded, kohAdded, reactionComplete].filter(Boolean).length;

  const handleSafety = useCallback(() => {
    if (!safetyAcknowledged) {
      setSafetyAcknowledged(true);
      setCurrentStep(2);
    }
  }, [safetyAcknowledged]);

  const handleAddAluminum = useCallback(() => {
    if (safetyAcknowledged && !aluminumAdded) {
      setAluminumAdded(true);
      setCurrentStep(3);
    }
  }, [safetyAcknowledged, aluminumAdded]);

  const handleAddKOH = useCallback(() => {
    if (aluminumAdded && !kohAdded) {
      setKohAdded(true);
      setReacting(true);
      setCurrentStep(4);

      // Simulate reaction
      setTimeout(() => {
        setReacting(false);
        setReactionComplete(true);
      }, 3000);
    }
  }, [aluminumAdded, kohAdded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: Aluminum Dissolution</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Safety Review', hint: 'KOH is caustic', done: safetyAcknowledged },
            { id: 2, title: 'Add Aluminum', hint: 'Add very small pieces of aluminum', done: aluminumAdded },
            { id: 3, title: 'Add KOH Solution', hint: 'Add potassium hydroxide', done: kohAdded },
            { id: 4, title: 'Observe Reaction', hint: 'Watch H₂ gas evolution', done: reactionComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Aluminum Dissolution"
        completedCount={completedSteps}
        totalSteps={4}
        equipment={['Beaker', 'Aluminum foil', 'KOH solution']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="dissolution-workspace">
          {!safetyAcknowledged && (
            <div className="safety-panel" onClick={handleSafety}>
              <AlertTriangle size={32} color="#FBBF24" />
              <h3>Safety Warning</h3>
              <p>KOH (potassium hydroxide) is a strong base and caustic.</p>
              <p>Hydrogen gas is flammable - no open flames!</p>
              <button className="acknowledge-btn">
                <Check size={16} />
                I Understand
              </button>
            </div>
          )}

          {safetyAcknowledged && (
            <div className="equipment-row">
              {/* Aluminum Foil */}
              <div
                className={`equipment-station ${currentStep === 2 && !aluminumAdded ? 'clickable' : ''}`}
                onClick={handleAddAluminum}
              >
                <svg viewBox="0 0 60 60" width={80} height={80}>
                  <rect x="10" y="15" width="40" height="30" fill="#C0C0C0" rx="2" />
                  <rect x="12" y="17" width="36" height="26" fill="#D8D8D8" />
                  {/* Crinkled texture */}
                  <line x1="15" y1="20" x2="25" y2="35" stroke="#A0A0A0" strokeWidth="0.5" />
                  <line x1="30" y1="18" x2="35" y2="40" stroke="#A0A0A0" strokeWidth="0.5" />
                  <line x1="40" y1="22" x2="45" y2="38" stroke="#A0A0A0" strokeWidth="0.5" />
                </svg>
                <span className="equipment-label">Aluminum Foil</span>
              </div>

              {/* Reaction Beaker */}
              <div
                className={`equipment-station ${currentStep === 3 && !kohAdded ? 'clickable' : ''}`}
                onClick={handleAddKOH}
              >
                <Beaker
                  size={400}
                  fillLevel={kohAdded ? 60 : aluminumAdded ? 10 : 0}
                  fillColor={reacting ? '#E8E8E8' : '#E3F2FD'}
                  label="400 mL"
                  active={reacting}
                />
                {/* Gas bubbles during reaction */}
                {reacting && (
                  <div className="bubbles">
                    <span className="bubble" style={{ left: '30%', animationDelay: '0s' }} />
                    <span className="bubble" style={{ left: '50%', animationDelay: '0.3s' }} />
                    <span className="bubble" style={{ left: '70%', animationDelay: '0.6s' }} />
                  </div>
                )}
                <span className="equipment-label">Reaction Beaker</span>
              </div>

              {/* KOH Bottle */}
              <div className="equipment-station">
                <svg viewBox="0 0 50 80" width={60} height={100}>
                  <rect x="10" y="20" width="30" height="50" rx="3" fill="#5A5A5A" />
                  <rect x="12" y="25" width="26" height="40" fill="#E3F2FD" opacity="0.8" />
                  <rect x="15" y="5" width="20" height="15" rx="2" fill="#333" />
                  <text x="25" y="55" fontSize="6" fill="#333" textAnchor="middle">KOH</text>
                </svg>
                <span className="equipment-label">KOH Solution</span>
              </div>
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {reacting && (
              <div className="status-message warning">
                <Flame size={20} />
                Reaction in progress - H₂ gas evolving. Keep away from flames!
              </div>
            )}
            {reactionComplete && (
              <div className="status-message success">
                <Check size={20} />
                Reaction complete! Aluminum dissolved forming aluminate ions.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Aluminum Dissolution in Base</h4>
            <p>
              2Al + 2K⁺ + 2OH⁻ + 6H₂O → 2K⁺ + 2Al(OH)₄⁻ + 3H₂↑
            </p>
            <p>
              Aluminum reacts with potassium hydroxide to form aluminate ions
              and hydrogen gas. The hydrogen evolution shows as bubbles.
            </p>
          </div>
        </div>

        <style>{`
          .dissolution-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .safety-panel {
            background: rgba(251, 191, 36, 0.1);
            border: 1px solid rgba(251, 191, 36, 0.3);
            border-radius: 12px;
            padding: 24px;
            max-width: 400px;
            text-align: center;
            cursor: pointer;
          }

          .safety-panel h3 {
            color: #FBBF24;
            margin: 12px 0;
          }

          .safety-panel p {
            color: var(--text-secondary);
            margin: 8px 0;
          }

          .acknowledge-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin-top: 16px;
            padding: 10px 20px;
            background: var(--secondary);
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
            position: relative;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .bubbles {
            position: absolute;
            bottom: 80px;
            left: 50%;
            transform: translateX(-50%);
            width: 60px;
            height: 40px;
          }

          .bubble {
            position: absolute;
            width: 6px;
            height: 6px;
            background: rgba(255, 255, 255, 0.8);
            border-radius: 50%;
            animation: rise 1s infinite ease-out;
          }

          @keyframes rise {
            0% {
              bottom: 0;
              opacity: 1;
            }
            100% {
              bottom: 40px;
              opacity: 0;
            }
          }

          .status-area {
            min-height: 50px;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.warning {
            background: rgba(251, 191, 36, 0.2);
            color: #FBBF24;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp8"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
